import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SoloShell from './SoloShell';
import { api } from './api';
import './BuzzerSolo.css';

function TableCard({ table, selected, onTap, onLongPress }) {
  const timerRef = useRef(null);
  const activeClass = selected ? ' selected' : '';

  const startPress = () => {
    timerRef.current = window.setTimeout(() => {
      onLongPress(table);
      timerRef.current = null;
    }, 550);
  };

  const cancelPress = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  return (
    <div
      className={`bsolo-table-card ${table.state}${activeClass}`}
      onClick={() => onTap(table)}
      onMouseDown={startPress}
      onMouseUp={cancelPress}
      onMouseLeave={cancelPress}
      onTouchStart={startPress}
      onTouchEnd={cancelPress}
    >
      <div className="bsolo-table-number">{table.number}</div>
      <div className="bsolo-meta">{table.capacity ? `${table.capacity} seats` : 'No seat limit'}</div>
      <div className="bsolo-status">
        {table.state === 'free' && 'Free'}
        {table.state === 'active' && 'Active'}
        {table.state === 'calling' && 'Calling service'}
      </div>
    </div>
  );
}

export default function BuzzerSoloFloorPage() {
  const [loading, setLoading] = useState(true);
  const [areas, setAreas] = useState([]);
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  const [tables, setTables] = useState([]);
  const [selectedTableIds, setSelectedTableIds] = useState([]);
  const [message, setMessage] = useState('');
  const [qrInfo, setQrInfo] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/buzzersolo/live-floor');
      setAreas(data.areas || []);
      setTables(data.tables || []);
      setSelectedAreaId((prev) => prev || data.areas?.[0]?.id || null);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setMessage(error?.response?.data?.error || 'Failed to load live floor');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = window.setInterval(load, 6000);
    return () => window.clearInterval(id);
  }, [load]);

  const visibleTables = useMemo(
    () => tables.filter((table) => !selectedAreaId || Number(table.areaId) === Number(selectedAreaId)),
    [tables, selectedAreaId]
  );

  const selectedCount = selectedTableIds.length;

  const onTap = async (table) => {
    setMessage('');
    if (table.state === 'free') {
      setSelectedTableIds((prev) => prev.includes(table.id)
        ? prev.filter((id) => id !== table.id)
        : [...prev, table.id]);
      return;
    }

    if (table.state === 'calling' && table.activeCallId) {
      try {
        await api.post(`/buzzersolo/calls/${table.activeCallId}/acknowledge`);
        await load();
        setMessage(`Call for table ${table.number} acknowledged.`);
      } catch (error) {
        setMessage(error?.response?.data?.error || 'Failed to acknowledge call');
      }
      return;
    }

    if (table.publicToken) {
      setQrInfo({ tableNumber: table.number, token: table.publicToken });
    }
  };

  const onLongPress = async (table) => {
    setMessage('');
    if (table.state === 'free') {
      const finalSelection = selectedTableIds.includes(table.id)
        ? selectedTableIds
        : [...selectedTableIds, table.id];

      try {
        const { data } = await api.post('/buzzersolo/sessions', { tableIds: finalSelection });
        setSelectedTableIds([]);
        setQrInfo({ tableNumber: finalSelection.join(', '), token: data.publicToken, url: data.publicUrl });
        await load();
      } catch (error) {
        setMessage(error?.response?.data?.error || 'Failed to start sitting');
      }
      return;
    }

    if (table.state === 'active' || table.state === 'calling') {
      if (!table.sessionId) return;
      if (!window.confirm(`Release sitting for table ${table.number}?`)) return;
      try {
        await api.post(`/buzzersolo/sessions/${table.sessionId}/release`);
        await load();
        setMessage(`Table ${table.number} released.`);
      } catch (error) {
        setMessage(error?.response?.data?.error || 'Failed to release sitting');
      }
    }
  };

  const startSelected = async () => {
    if (!selectedTableIds.length) return;
    try {
      const { data } = await api.post('/buzzersolo/sessions', { tableIds: selectedTableIds });
      setSelectedTableIds([]);
      setQrInfo({ tableNumber: selectedTableIds.join(', '), token: data.publicToken, url: data.publicUrl });
      await load();
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Failed to start sitting');
    }
  };

  return (
    <SoloShell
      title="BuzzerFeedback Solo"
      subtitle="Tap to select. Long-press a selected free table to start a sitting. Long-press an active table to release it."
      actions={<button className="bsolo-btn-secondary" onClick={load}>Refresh</button>}
    >
      {message ? <div className="bsolo-card" style={{ marginBottom: 16 }}>{message}</div> : null}

      {loading ? <div className="bsolo-card">Loading…</div> : null}

      {!!areas.length && (
        <div className="bsolo-tabs">
          {areas.map((area) => (
            <button
              key={area.id}
              className={`bsolo-tab ${Number(selectedAreaId) === Number(area.id) ? 'active' : ''}`}
              onClick={() => setSelectedAreaId(area.id)}
            >
              {area.name}
            </button>
          ))}
        </div>
      )}

      <div className="bsolo-grid cols-2">
        <div className="bsolo-panel">
          <div className="bsolo-table-grid">
            {visibleTables.map((table) => (
              <TableCard
                key={table.id}
                table={table}
                selected={selectedTableIds.includes(table.id)}
                onTap={onTap}
                onLongPress={onLongPress}
              />
            ))}
            {!visibleTables.length && <div className="bsolo-empty">No tables found for this area.</div>}
          </div>
        </div>

        <div className="bsolo-stack">
          <div className="bsolo-panel">
            <h3 style={{ marginTop: 0 }}>Session QR</h3>
            <div className="bsolo-qr-box">
              {qrInfo ? (
                <div>
                  <div style={{ fontWeight: 800, marginBottom: 10 }}>Tables {qrInfo.tableNumber}</div>
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(qrInfo.url || `${window.location.origin}/buzz-solo/${qrInfo.token}`)}`}
                    alt="Session QR"
                    width="220"
                    height="220"
                    style={{ display: 'block', margin: '0 auto 14px', borderRadius: 16 }}
                  />
                  <div style={{ fontSize: '0.9rem', wordBreak: 'break-all' }}>{qrInfo.url || `${window.location.origin}/buzz-solo/${qrInfo.token}`}</div>
                  <div className="bsolo-meta" style={{ marginTop: 12 }}>
                    For production, replace the external QR image URL with an in-app QR component if you want zero third-party dependency.
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontWeight: 800 }}>No active QR selected yet.</div>
                  <div className="bsolo-meta" style={{ marginTop: 10 }}>Start or open a sitting to display its session-specific QR.</div>
                </div>
              )}
            </div>
          </div>

          <div className="bsolo-panel">
            <h3 style={{ marginTop: 0 }}>Quick rules</h3>
            <div className="bsolo-list bsolo-muted">
              <div>• Every sitting creates a fresh QR token.</div>
              <div>• Acknowledging a call stops the visual alert.</div>
              <div>• Releasing the sitting unlocks feedback for the customer.</div>
            </div>
          </div>
        </div>
      </div>

      {selectedCount > 0 && (
        <div className="bsolo-bottom-bar">
          <div>{selectedCount} table(s) selected</div>
          <div className="bsolo-actions" style={{ marginTop: 0 }}>
            <button className="bsolo-btn-secondary" onClick={() => setSelectedTableIds([])}>Clear</button>
            <button className="bsolo-btn" onClick={startSelected}>Start sitting</button>
          </div>
        </div>
      )}
    </SoloShell>
  );
}
